// app/javascript/controllers/accounts_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "addButton",
    // Users
    "usersPanel","usersForm","usersActionsBar","usersExportSortActions","usersApplyActions","usersExportLink","usersCount",
    // Businesses
    "businessesPanel","businessesForm","businessesActionsBar","businessesExportSortActions","businessesApplyActions","businessesExportLink","businessesCount",
  ]

  static values = {
    activeTab: String,
    newPaths: Object,
    usersBaseUrl: String,
    businessesBaseUrl: String,
  }

  // ===== Add button =====
  goToAdd(e) {
    e.preventDefault()
    const tab = this.activeTabValue || "admin_users"
    const path = this.newPathsValue?.[tab]
    if (path) window.location.assign(path)
  }

  // ===== Users filter panel =====
  toggleUsersPanel() {
    if (!this.hasUsersPanelTarget) return
    this.usersPanelTarget.classList.toggle("hidden")
    this._syncUsersActionsBar()
  }

  clearUsersFilters(e) {
    if (e) e.preventDefault()
    if (!this.hasUsersFormTarget) return

    // 1) Clear all inputs
    this._usersClearFormValues()

    // 2) Navigate to clean URL: base + ?tab=users
    const url = (this.usersBaseUrlValue || "/admin/accounts") + "?tab=users"
    window.location.assign(url)
  }

  // alias for the Reset button (same behavior)
  resetUsersFilters(e) {
    this.clearUsersFilters(e)
  }

  onUsersInputChanged() {
    this._updateUsersExportHref()
    this._updateUsersFiltersCount()
    this._syncUsersActionsBar()
  }

  // ===== private helpers =====
  _syncUsersActionsBar() {
    if (!(this.hasUsersActionsBarTarget && this.hasUsersExportSortActionsTarget && this.hasUsersApplyActionsTarget)) return
    const panelOpen = this.hasUsersPanelTarget && !this.usersPanelTarget.classList.contains("hidden")
    this.usersExportSortActionsTarget.classList.toggle("hidden", panelOpen)
    this.usersApplyActionsTarget.classList.toggle("hidden", !panelOpen)
  }

  _updateUsersExportHref() {
    if (!(this.hasUsersExportLinkTarget && this.hasUsersFormTarget)) return
    const params = this._usersSerializeForm()
    params.set("tab", "users")
    const url = (this.usersBaseUrlValue || "/admin/accounts") + "?" + params.toString()
    this.usersExportLinkTarget.setAttribute("href", url + "&format=csv")
  }

  _updateUsersFiltersCount() {
    if (!(this.hasUsersFormTarget && this.hasUsersCountTarget)) return
    const active = this._usersActiveFilterCount()
    this.usersCountTarget.textContent = active > 0 ? `Filters (${active})` : "Filters"
  }

  _usersActiveFilterCount() {
    // Count non-empty params that matter (exclude boilerplate)
    const ignore = new Set(["tab", "sort", "page", "users_page", "commit", "authenticity_token"])
    const params = this._usersSerializeForm()
    let count = 0

    for (const [key, value] of params.entries()) {
      if (ignore.has(key)) continue
      // treat empty string or null as not set
      if (value == null || String(value).trim() === "") continue
      count++
    }
    return count
  }

  _usersSerializeForm() {
    return new URLSearchParams(new FormData(this.usersFormTarget))
  }

  _usersClearFormValues() {
    const form = this.usersFormTarget
    Array.from(form.elements).forEach(el => {
      if (!el.name) return
      if (el.name === "tab") return // keep tab
      if (el.type === "checkbox" || el.type === "radio") el.checked = false
      else if (["select-one", "select-multiple"].includes(el.type)) el.selectedIndex = 0
      else if (["text", "search", "number", "date", "email", "tel", "hidden"].includes(el.type)) el.value = ""
      else if (el.tagName === "TEXTAREA") el.value = ""
    })
  }

   // ===== BUSINESSES panel toggling =====
  toggleBusinessesPanel() {
    if (!this.hasBusinessesPanelTarget) return
    this.businessesPanelTarget.classList.toggle("hidden")
    this._syncBusinessesActionsBar()
  }

  clearBusinessesFilters(e) {
    if (e) e.preventDefault()
    if (!this.hasBusinessesFormTarget) return
    this._businessesClearFormValues()
    const url = (this.businessesBaseUrlValue || "/admin/accounts") + "?tab=businesses"
    window.location.assign(url)
  }

  resetBusinessesFilters(e) {
    this.clearBusinessesFilters(e)
  }

  onBusinessesInputChanged() {
    this._updateBusinessesExportHref()
    this._updateBusinessesFiltersCount()
    this._syncBusinessesActionsBar()
  }

  connect() {
    // Keep your existing users init
    if (this.hasUsersFormTarget) {
      this._updateUsersExportHref()
      this._updateUsersFiltersCount()
      this._syncUsersActionsBar()
    }
    // Init businesses too
    if (this.hasBusinessesFormTarget) {
      this._updateBusinessesExportHref()
      this._updateBusinessesFiltersCount()
      this._syncBusinessesActionsBar()
    }
  }

  // ===== private: Businesses =====
  _syncBusinessesActionsBar() {
    if (!(this.hasBusinessesActionsBarTarget && this.hasBusinessesExportSortActionsTarget && this.hasBusinessesApplyActionsTarget)) return
    const open = this.hasBusinessesPanelTarget && !this.businessesPanelTarget.classList.contains("hidden")
    this.businessesExportSortActionsTarget.classList.toggle("hidden", open)
    this.businessesApplyActionsTarget.classList.toggle("hidden", !open)
  }

  _updateBusinessesExportHref() {
    if (!(this.hasBusinessesExportLinkTarget && this.hasBusinessesFormTarget)) return
    const params = this._businessesSerializeForm()
    params.set("tab", "businesses")
    const url = (this.businessesBaseUrlValue || "/admin/accounts") + "?" + params.toString()
    this.businessesExportLinkTarget.setAttribute("href", url + "&format=csv")
  }

  _updateBusinessesFiltersCount() {
    if (!(this.hasBusinessesFormTarget && this.hasBusinessesCountTarget)) return
    const active = this._businessesActiveFilterCount()
    this.businessesCountTarget.textContent = active > 0 ? `Filters (${active})` : "Filters"
  }

  _businessesActiveFilterCount() {
    const ignore = new Set(["tab", "sort", "page", "businesses_page", "commit", "authenticity_token"])
    const params = this._businessesSerializeForm()
    let count = 0
    for (const [key, value] of params.entries()) {
      if (ignore.has(key)) continue
      if (value == null || String(value).trim() === "") continue
      count++
    }
    return count
  }

  _businessesSerializeForm() {
    return new URLSearchParams(new FormData(this.businessesFormTarget))
  }

  _businessesClearFormValues() {
    const form = this.businessesFormTarget
    Array.from(form.elements).forEach(el => {
      if (!el.name) return
      if (el.name === "tab") return
      if (el.type === "checkbox" || el.type === "radio") el.checked = false
      else if (["select-one", "select-multiple"].includes(el.type)) el.selectedIndex = 0
      else if (["text","search","number","date","email","tel","hidden"].includes(el.type)) el.value = ""
      else if (el.tagName === "TEXTAREA") el.value = ""
    })
  }
}
